import { useEffect } from 'react';
import { createDocEntryStore } from './docStore';
import { fetchPublishStatus, type PublishStatus } from './publishApi';

// The document's publish state, shared by Phase 5 (the publish panel) and the
// phase-progress bar. The bar needs `updatedAt` to tell a page published from the
// current clips apart from one published before they existed, so both readers go
// through the same store rather than each polling `/publish/status` on its own.
type Snapshot = PublishStatus & { loaded: boolean };

const EMPTY: Snapshot = {
  published: false, url: null, updatedAt: null, runId: null, loaded: false,
};

const store = createDocEntryStore<Snapshot, { inFlight: boolean }>(() => ({
  snapshot: EMPTY,
  inFlight: false,
}));

export async function refreshPublishStatus(docId: string): Promise<void> {
  if (!docId) return;
  const entry = store.getEntry(docId);
  if (entry.inFlight) return;
  entry.inFlight = true;
  try {
    const status = await fetchPublishStatus(docId);
    entry.snapshot = { ...status, loaded: true };
  } catch {
    // Treat as not-published; the publish buttons still work.
    entry.snapshot = { ...EMPTY, loaded: true };
  } finally {
    entry.inFlight = false;
    store.notify(docId);
  }
}

export function usePublishStatus(docId: string): Snapshot {
  const snap = store.useSnapshot(docId);
  useEffect(() => {
    if (docId && !store.get(docId).loaded) void refreshPublishStatus(docId);
  }, [docId]);
  return snap;
}
